// FXC (G10 cross rates), CMDTY (commodity futures) and CRYPTO.

import { quotes, type Quote } from "../api";
import { clear, h, toggles } from "../dom";
import * as f from "../fmt";
import { quoteMonitor, type Row } from "../monitor";
import { register, type Ctx } from "../registry";

// --- FXC --------------------------------------------------------------------

const G10 = ["USD", "EUR", "JPY", "GBP", "CHF", "CAD", "AUD", "NZD", "SEK", "NOK"];
/// Yahoo's USD pair per currency, and whether it is quoted as USD per unit
/// (EURUSD=X) rather than units per USD (JPY=X is USDJPY).
const USD_PAIR: Record<string, [string, boolean]> = {
  EUR: ["EURUSD=X", true],
  GBP: ["GBPUSD=X", true],
  AUD: ["AUDUSD=X", true],
  NZD: ["NZDUSD=X", true],
  JPY: ["JPY=X", false],
  CHF: ["CHF=X", false],
  CAD: ["CAD=X", false],
  SEK: ["SEK=X", false],
  NOK: ["NOK=X", false],
};
/// Market convention: of two currencies, the one listed first is the base.
const BASE_ORDER = ["EUR", "GBP", "AUD", "NZD", "USD", "CAD", "CHF", "NOK", "SEK", "JPY"];

const FX_ROWS: Row[] = [
  { sym: "", group: "Dollar" },
  { sym: "EURUSD=X" },
  { sym: "JPY=X" },
  { sym: "GBPUSD=X" },
  { sym: "CHF=X" },
  { sym: "CAD=X" },
  { sym: "AUDUSD=X" },
  { sym: "NZDUSD=X" },
  { sym: "SEK=X" },
  { sym: "NOK=X" },
  { sym: "DX-Y.NYB" },
  { sym: "", group: "Crosses" },
  { sym: "EURJPY=X" },
  { sym: "EURGBP=X" },
  { sym: "EURCHF=X" },
  { sym: "GBPJPY=X" },
  { sym: "AUDJPY=X" },
  { sym: "EURNOK=X" },
  { sym: "EURSEK=X" },
];

/// Five significant digits: 1.1244, 157.99, 0.0063294.
const fx = (v: number) => v.toLocaleString("en-US", { minimumSignificantDigits: 5, maximumSignificantDigits: 5 });

function runFXC(ctx: Ctx): void {
  let view: "rate" | "pct" = ctx.args.includes("PCT") ? "pct" : "rate";
  let qs = new Map<string, Quote>();
  const toolbar = h("div", { class: "toolbar" });
  const matrix = h("div", { style: { padding: "4px 8px 8px", overflowX: "auto" } });

  /// USD value of one unit, now and at the previous close.
  const usd = (ccy: string): [number, number] | null => {
    if (ccy === "USD") return [1, 1];
    const [sym, direct] = USD_PAIR[ccy];
    const q = qs.get(sym);
    const last = q?.regularMarketPrice;
    const prev = q?.regularMarketPreviousClose;
    if (!last || !prev) return null;
    return direct ? [last, prev] : [1 / last, 1 / prev];
  };

  const drawToolbar = () =>
    clear(
      toolbar,
      ...toggles(
        [
          ["rate", "Rates"],
          ["pct", "% change"],
        ] as ["rate" | "pct", string][],
        view,
        (v) => ((view = v), drawToolbar(), draw()),
      ),
      h("span", { class: "dim" }, "  1 unit of the row currency in the column currency. Click a cell to chart the pair."),
    );
  const draw = () => {
    const vals = new Map(G10.map((c) => [c, usd(c)]));
    const head = h("tr", null, h("th", null, ""), ...G10.map((c) => h("th", { class: "num" }, c)));
    const body = G10.map((row) =>
      h(
        "tr",
        null,
        h("td", { class: "amber" }, row),
        ...G10.map((col) => {
          if (row === col) return h("td", { style: { background: "var(--bg3)" } }, "");
          const a = vals.get(row);
          const b = vals.get(col);
          if (!a || !b) return h("td", { class: "num dim" }, f.NA);
          const rate = a[0] / b[0];
          const pct = (rate / (a[1] / b[1]) - 1) * 100;
          const [base, quote] = BASE_ORDER.indexOf(row) < BASE_ORDER.indexOf(col) ? [row, col] : [col, row];
          const cmd = `${base}${quote}=X GP`;
          return h(
            "td",
            {
              class: `num ${view === "pct" ? f.cls(pct) : ""}`,
              style: { cursor: "pointer" },
              title: `${row}${col} ${fx(rate)}  ${f.pct(pct)}  →  ${base}${quote} GP`,
              onclick: (e: MouseEvent) => (e.shiftKey ? ctx.panel.goOther(cmd) : ctx.panel.go(cmd)),
            },
            view === "rate" ? fx(rate) : f.pct(pct),
          );
        }),
      ),
    );
    clear(matrix, h("table", { class: "dt", style: { width: "auto" } }, h("thead", null, head), h("tbody", null, ...body)));
  };

  // Same symbol list and order as the monitor below, so both share one
  // cached quote request on the server.
  const syms = FX_ROWS.filter((r) => r.sym).map((r) => r.sym);
  const load = async () => {
    const r = await quotes(syms, ctx.signal);
    qs = new Map(r.data.map((q) => [q.symbol, q]));
    draw();
  };
  const mon = quoteMonitor(ctx, FX_ROWS, {
    cols: ["last", "chg", "pct", "ytd", "spark", "range", "time"],
    dp: (s) => (s.includes("JPY") ? 3 : s === "DX-Y.NYB" ? 2 : 4),
    action: (s) => `${s} GP`,
  });
  drawToolbar();
  draw();
  load().catch(() => {});
  ctx.panel.every(15_000, load);
  clear(ctx.body, toolbar, matrix, mon.el);
}

// --- CMDTY ------------------------------------------------------------------

/// Front-month futures with their quoting unit.
const CMDTY: [string, string][] = [
  ["Energy", ""],
  ["CL=F", "USD/bbl"],
  ["BZ=F", "USD/bbl"],
  ["NG=F", "USD/MMBtu"],
  ["HO=F", "USD/gal"],
  ["RB=F", "USD/gal"],
  ["Metals", ""],
  ["GC=F", "USD/ozt"],
  ["SI=F", "USD/ozt"],
  ["HG=F", "USD/lb"],
  ["PL=F", "USD/ozt"],
  ["PA=F", "USD/ozt"],
  ["Grains", ""],
  ["ZC=F", "USc/bu"],
  ["ZW=F", "USc/bu"],
  ["KE=F", "USc/bu"],
  ["ZS=F", "USc/bu"],
  ["ZM=F", "USD/st"],
  ["ZL=F", "USc/lb"],
  ["Softs", ""],
  ["KC=F", "USc/lb"],
  ["SB=F", "USc/lb"],
  ["CC=F", "USD/t"],
  ["CT=F", "USc/lb"],
  ["OJ=F", "USc/lb"],
  ["Livestock", ""],
  ["LE=F", "USc/lb"],
  ["GF=F", "USc/lb"],
  ["HE=F", "USc/lb"],
];

function runCMDTY(ctx: Ctx): void {
  const unit = new Map(CMDTY);
  const rows: Row[] = CMDTY.map(([sym, u]) => (u ? { sym } : { sym: "", group: sym }));
  const mon = quoteMonitor(ctx, rows, {
    cols: ["last", "chg", "pct", "ytd", "spark", "range", "time"],
    action: (s) => `${s} GP`,
    extra: [
      { label: "Unit", get: (i) => unit.get(i.row.sym) ?? "", cls: () => "dim", sort: false },
      { label: "Expiry", get: (i) => i.q?.expireDate, fmt: (v) => f.utcDate(v), cls: () => "dim", title: "Front contract's last trading day" },
    ],
  });
  clear(ctx.body, mon.el);
}

// --- CRYPTO -----------------------------------------------------------------

/// Label, Yahoo symbol (most are `XXX-USD`), name.
const coin = (label: string, name: string, sym = `${label}-USD`): Row => ({ sym, label, name });

const CRYPTO_ROWS: Row[] = [
  { sym: "", group: "Majors" },
  coin("BTC", "Bitcoin"),
  coin("ETH", "Ether"),
  { sym: "", group: "Large caps" },
  coin("XRP", "XRP"),
  coin("BNB", "BNB"),
  coin("SOL", "Solana"),
  coin("TRX", "Tron"),
  coin("DOGE", "Dogecoin"),
  coin("ADA", "Cardano"),
  coin("HYPE", "Hyperliquid", "HYPE32196-USD"),
  coin("LINK", "Chainlink"),
  coin("XMR", "Monero"),
  coin("XLM", "Stellar"),
  coin("BCH", "Bitcoin Cash"),
  { sym: "", group: "Mid caps" },
  coin("NEAR", "NEAR Protocol"),
  coin("TON", "Toncoin", "TON11419-USD"),
  coin("LTC", "Litecoin"),
  coin("UNI", "Uniswap", "UNI7083-USD"),
  coin("AVAX", "Avalanche"),
  coin("SUI", "Sui", "SUI20947-USD"),
  coin("HBAR", "Hedera"),
  coin("SHIB", "Shiba Inu"),
  coin("AAVE", "Aave"),
  coin("DOT", "Polkadot"),
  { sym: "", group: "Stablecoins" },
  coin("USDT", "Tether"),
  coin("USDC", "USD Coin"),
  coin("DAI", "Dai"),
  { sym: "", group: "Listed proxies" },
  { sym: "IBIT", name: "iShares Bitcoin Trust" },
  { sym: "ETHA", name: "iShares Ethereum Trust" },
  { sym: "MSTR", name: "Strategy" },
  { sym: "COIN", name: "Coinbase" },
];

function runCRYPTO(ctx: Ctx): void {
  const mon = quoteMonitor(ctx, CRYPTO_ROWS, {
    cols: ["last", "chg", "pct", "mcap", "ytd", "spark", "range"],
    // Yahoo's priceHint of 5 would show SHIB (~0.000005) as 0.00001.
    dp: (s) => (s === "SHIB-USD" ? 8 : undefined),
    action: (s) => `${s} GP`,
    extra: [
      {
        label: "Vol 24h",
        // Coins report 24h dollar volume; for listed proxies use the day's turnover.
        get: (i) => i.q?.volume24Hr ?? (i.q?.regularMarketVolume && i.q.regularMarketPrice ? i.q.regularMarketVolume * i.q.regularMarketPrice : undefined),
        fmt: (v) => f.big(v),
        num: true,
        title: "24-hour volume in USD (listed proxies: today's dollar turnover)",
      },
    ],
  });
  clear(ctx.body, mon.el);
}

register(
  {
    code: "FXC",
    name: "FX Cross Rates",
    group: "Markets",
    needsSec: false,
    help: "G10 cross-rate matrix and major pairs. Click a cell to chart the pair; FXC PCT for % changes.",
    aliases: ["FXMON"],
    run: runFXC,
  },
  {
    code: "CMDTY",
    name: "Commodity Futures",
    group: "Markets",
    needsSec: false,
    help: "Front-month energy, metals, grains, softs and livestock futures.",
    aliases: ["GLCO"],
    run: runCMDTY,
  },
  {
    code: "CRYPTO",
    name: "Crypto Monitor",
    group: "Markets",
    needsSec: false,
    help: "Major coins and stablecoins with market cap and 24h volume, plus listed proxies.",
    aliases: ["COINS"],
    run: runCRYPTO,
  },
);
